import { Controller } from '@hotwired/stimulus';

/**
 * Opens or closes a generic modal (data-controller="modal") by its DOM ID
 * from outside the modal's Stimulus controller scope.
 *
 * For confirmation modals (data-controller="confirmation-modal"), use
 * confirmation-modal-trigger instead.
 *
 * Usage (static modal):
 *   <button data-controller="modal-opener"
 *           data-modal-opener-id-value="my-modal"
 *           data-action="click->modal-opener#open">
 *     Open Modal
 *   </button>
 *
 * Usage (lazy-loaded via Turbo Frame):
 *   <tr data-controller="modal-opener"
 *       data-modal-opener-id-value="shared-modal"
 *       data-modal-opener-url-value="/items/42"
 *       data-action="click->modal-opener#open">
 *   </tr>
 */
export default class extends Controller {
  static values = { id: String, url: String };

  connect() {
    if (this.element.tagName === 'TR') {
      this.element.setAttribute('tabindex', '0');
    }
  }

  open() {
    if (this.hasUrlValue) {
      this.#loadFrameContent();
    }
    const controller = this.#findModalController();
    if (controller) controller.open();
  }

  close() {
    const controller = this.#findModalController();
    if (controller) controller.close();
  }

  #loadFrameContent() {
    const modal = document.getElementById(this.idValue);
    if (!modal) return;

    const frame = modal.querySelector('turbo-frame');
    if (!frame) return;

    // Restore loading indicator from template if available
    const template = document.getElementById(`${this.idValue}-loading`);
    if (template) {
      frame.replaceChildren(template.content.cloneNode(true));
    } else {
      frame.replaceChildren();
    }

    frame.src = this.urlValue;
  }

  openKeyboard(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  #findModalController() {
    const modal = document.getElementById(this.idValue);
    if (!modal) return null;
    return this.application.getControllerForElementAndIdentifier(modal, 'modal');
  }
}
